import { useThree } from '@react-three/fiber';
import { useCallback, useEffect, useRef } from 'react';
import { Vector2, Vector3 } from 'three';
import { useShallow } from 'zustand/shallow';

import { CameraProjectionType } from 'app/modules/wallforming/components/scenes/camera-controls/types/cameraProjectionType';
import { raycastPlane } from 'app/modules/wallforming/components/scenes/objects/articles/utils/dragUtils';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

export const useMainSceneEventHandler = () => {
    const {
        handleOnWindowKeyDown,
        handleOnWindowKeyUp,
        handleOnWindowPointerUp,
        handleOnWindowPointerDown,
        handleOnDragArticle,
        setPointerPositionRef,
        mainRaycasterRef,
        mainCameraProjectionType
    } = usePlacementStore(
        useShallow((state) => ({
            handleOnWindowKeyDown: state.handleOnWindowKeyDown,
            handleOnWindowKeyUp: state.handleOnWindowKeyUp,
            handleOnWindowPointerUp: state.handleOnWindowPointerUp,
            handleOnWindowPointerDown: state.handleOnWindowPointerDown,
            handleOnDragArticle: state.handleOnDragArticle,
            setPointerPositionRef: state.setPointerPositionRef,
            mainRaycasterRef: state.mainRaycasterRef,
            mainCameraProjectionType: state.mainCameraProjectionType
        }))
    );

    const initializeSelectionBox = usePlacementStore(useShallow((state) => state.initializeSelectionBox));

    const scene = useThree((state) => state.scene);
    const camera = useThree((state) => state.camera);
    const gl = useThree((state) => state.gl);

    const ndcPointer = useRef<Vector2>(new Vector2());
    const pointerPlanePoint = useRef<Vector3>(new Vector3());
    const canvasRectRef = useRef<DOMRect | null>(null);
    const prevProjectionTypeRef = useRef<CameraProjectionType>(mainCameraProjectionType);

    const handleWindowPointerUp = useCallback(
        (event: PointerEvent) => {
            handleOnWindowPointerUp(event);
        },
        [handleOnWindowPointerUp]
    );

    const handleWindowPointerDown = useCallback(
        (event: PointerEvent) => {
            handleOnWindowPointerDown(event);
        },
        [handleOnWindowPointerDown]
    );

    useEffect(() => {
        if (prevProjectionTypeRef.current !== mainCameraProjectionType) {
            prevProjectionTypeRef.current = mainCameraProjectionType;
            initializeSelectionBox(scene);
        }
    }, [initializeSelectionBox, scene, mainCameraProjectionType]);

    useEffect(() => {
        initializeSelectionBox(scene);
        window.addEventListener('pointerup', handleWindowPointerUp);
        window.addEventListener('pointerdown', handleWindowPointerDown);

        return () => {
            window.removeEventListener('pointerup', handleWindowPointerUp);
            window.removeEventListener('pointerdown', handleWindowPointerDown);
        };
    }, [handleWindowPointerUp, handleWindowPointerDown, initializeSelectionBox, scene]);

    useEffect(() => {
        const canvas = gl.domElement;

        const updateCanvasRect = () => {
            canvasRectRef.current = canvas.getBoundingClientRect();
        };

        updateCanvasRect();
        canvas.addEventListener('pointerenter', updateCanvasRect);
        window.addEventListener('resize', updateCanvasRect);
        window.addEventListener('scroll', updateCanvasRect, true);

        return () => {
            canvas.removeEventListener('pointerenter', updateCanvasRect);
            window.removeEventListener('resize', updateCanvasRect);
            window.removeEventListener('scroll', updateCanvasRect, true);
        };
    }, [gl.domElement]);

    const handleCanvasPointerMove = useCallback(
        (event: PointerEvent) => {
            const raycaster = mainRaycasterRef.current;
            const canvas = gl.domElement;
            if (!raycaster || event.target !== canvas) {
                return;
            }

            const rect = canvasRectRef.current ?? canvas.getBoundingClientRect();
            canvasRectRef.current = rect;
            if (rect.width <= 0 || rect.height <= 0) {
                return;
            }

            ndcPointer.current.set(
                ((event.clientX - rect.left) / rect.width) * 2 - 1,
                -((event.clientY - rect.top) / rect.height) * 2 + 1
            );

            raycaster.setFromCamera(ndcPointer.current, camera);

            if (raycaster.ray.intersectPlane(raycastPlane, pointerPlanePoint.current)) {
                setPointerPositionRef(pointerPlanePoint.current);
            } else {
                setPointerPositionRef(null);
            }

            handleOnDragArticle({
                buttons: event.buttons,
                clientX: event.clientX,
                clientY: event.clientY,
                ray: raycaster.ray,
                altKey: event.altKey
            });
        },
        [camera, gl.domElement, handleOnDragArticle, mainRaycasterRef, setPointerPositionRef]
    );

    const handleCanvasPointerLeave = useCallback(() => {
        setPointerPositionRef(null);
    }, [setPointerPositionRef]);

    useEffect(() => {
        const canvas = gl.domElement;
        canvas.addEventListener('pointermove', handleCanvasPointerMove);
        canvas.addEventListener('pointerleave', handleCanvasPointerLeave);

        return () => {
            canvas.removeEventListener('pointermove', handleCanvasPointerMove);
            canvas.removeEventListener('pointerleave', handleCanvasPointerLeave);
        };
    }, [gl.domElement, handleCanvasPointerLeave, handleCanvasPointerMove]);

    useEffect(() => {
        window.addEventListener('keydown', handleOnWindowKeyDown, true);
        window.addEventListener('keyup', handleOnWindowKeyUp);
        return () => {
            window.removeEventListener('keydown', handleOnWindowKeyDown, true);
            window.removeEventListener('keyup', handleOnWindowKeyUp);
        };
    }, [handleOnWindowKeyDown, handleOnWindowKeyUp]);
};
